/**
 * THE STUDENT EXAM PAGE, server side — read an exam the way a student answers it, and turn what they
 * filled in into a `Submission` the existing grading pipeline already knows how to read.
 *
 * WHY THIS IS NOT THE SCAN PATH. An uploaded exam arrives as pixels and has to be split, OCR'd and
 * matched back onto tasks before anything can grade it (submission-split.ts → answer-extraction.job.ts →
 * process-submission.ts). A student answering online hands us the structure directly: we already know
 * which task each answer belongs to, because a widget on the page owns exactly one task. So this writes
 * the SAME shape those three steps would have produced and skips all of them — `splitConfirmed: true`
 * because there is no split to confirm, `taskSubmissions` populated on insert because there is nothing
 * to extract. `readySubmissionIds` (exam-utils.ts) picks it up on `taskSubmissions.0` existing, so the
 * teacher's normal grading run finds it like any other submission.
 *
 * ACCESS CONTROL IS AN EXPLICIT CHECK at the top of each resolver (`canAccessExam` → `loadAndAssert`,
 * authz/access.ts): only an exam the session owns opens or takes a hand-in. A foreign id reads exactly like
 * an unknown one (`noAccess`), so the page never tells a caller whether an exam exists. The session check
 * before it exists only to turn "no session" into a readable message rather than a denial.
 *
 * WHAT AN ANSWER HAS TO LOOK LIKE FOR GRADING TO SEE IT (default-grading.ts's `walkTask`): a task counts
 * as answered when it has at least one bounding box OR a non-empty `studentAnswer`, and the payload is
 * built from `boundingBoxes[]` — `contentType: 'text'` boxes contribute their `ocrExtractedText`,
 * `contentType: 'image'` boxes contribute their `imageId` as a real image part. That is why a typed answer
 * becomes a text box (not just a bare `studentAnswer` string): it keeps the teacher's grading canvas able
 * to show and edit it through `updateSubmissionTaskMutation`, which addresses boxes by index.
 *
 * AND WHERE IT WAS WRITTEN. The submit compiles the exam's own source a SECOND time with the answers
 * printed into it (see `ExamAnswers` and `ANSWERS` in typst/exam.typ), and that render is the submission:
 * its pages become `imageIds`, and its answer regions — which a long answer has already grown — become
 * each box's geometry. So an online submission ends up the same shape as a scanned one, pages plus boxes
 * onto them, which is what stops it being a special case everywhere a submission is displayed.
 */

import { AccessDeniedError } from '@adornis/abac/errors/AccessDeniedError.js';
import { logger } from '@adornis/base/logging.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerMutation, registerQuery } from '@adornis/baseql/metadata/register.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ImageFile } from '@adornis/file-utils/db/images.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import * as crypto from 'node:crypto';
import { loadAndAssert } from '../authz/access.js';
import { BoundingBox } from '../BoundingBox.js';
import { Exam } from '../exam.js';
import { Point } from '../Point.js';
// TYPE-ONLY, deliberately: `compile-exam.js` pulls node builtins and the native typst addon in at module
// scope, and this file is in the client bundle (the page imports its query functions). The compiler is
// reached through a dynamic import inside the resolver instead — the same seam `getExamSvgQuery` uses.
import type { ExamAnswers } from './exam-creation/compile-exam.js';
import type { AnswerRegion, BlockBoundingBox, ExamLayout } from './exam-creation/exam-structure.js';
import { splitEvaluationResult, Submission, SubmissionTask, TaskGradingState } from '../submission.js';
import { isExamCreationConfirmed } from '../utils/exam-lock.js';
import { aggregateOCRFromBoundingBoxes } from '../utils/exam-utils.js';

/** What the student page renders. A JSON string rather than an entity for the same reason
 * `getExamTasksTreeQuery` is one: `tasks` nests arbitrarily deep through `subTasks`, and a BaseQL
 * selection set has to name every level it wants. */
export interface StudentExamPayload {
  ok: boolean;
  /**
   * A TRANSLATION KEY, not a sentence — the page renders it through `t(...)`. A resolver has no access to
   * the viewer's language (i18n lives in the client), so the one place that knows it does the wording.
   */
  message?: string;
  title?: string;
  subject?: string;
  class?: string;
  duration?: string;
  date?: string;
  /** `Exam.tasks`, verbatim — task numbers are RELATIVE per level ("a", not "1.a"), see assembleTaskNodes. */
  tasks?: any[];
  /** `Exam.additionalInformation`, verbatim — materials referenced by tasks via `additionalInfoIds`. */
  additionalInformation?: any[];
  /** The printed page itself, as typst's rich SVG — the layer the answer inputs are placed on top of. */
  svg?: string;
  /**
   * Page size and every block's box, INCLUDING `answers` — where each task's answer region sits and what
   * kind it is. Produced by the same compile as `svg` above, deliberately: the persisted
   * `Exam.examLayoutJson` is from the last save, and positioning inputs from one compile onto a picture
   * from another is how a widget ends up half a centimetre off its own lines.
   */
  layout?: ExamLayout;
  /**
   * One PNG per printed page, straight off `Exam.blankExamIds` — the SAME rasters the grading and
   * structure-editor views already show as plain `<img>` tags, generated once when the exam was last
   * saved. Only the drawing editor's own printed backdrop uses these; everything ELSE on this page (the
   * exam display itself, every other widget's position) still comes from the fresh `svg`/`layout` above.
   *
   * DELIBERATELY NOT RE-RASTERIZED HERE. A page image is a raster, so cropping and scaling it in the
   * browser is cheap regardless of how it was produced (see `DrawingCanvas`'s own header) — the earlier
   * version of this feature learned that lesson the hard way by cropping the *live* SVG instead, which
   * meant re-rasterizing thousands of vector paths on every stroke. Rasterizing a PDF page ourselves,
   * though, costs real wall-clock time (GraphicsMagick/Ghostscript), and doing that per student per page
   * load — rather than once, at save time, like `blankExamIds` already is — would trade a drawing-time
   * cost for a load-time one. The accepted trade: if a teacher edits an exam's printed content after the
   * last save (rare — most exams aren't touched again once shared) and a student was already using the
   * old version, this backdrop can be a save behind until the next save regenerates it; the exact same
   * window every other viewer of `blankExamIds` already lives with, and no worse.
   */
  pageImageUrls?: string[];
}

/** The exam must be the session's own. A denial (incl. an unknown id) becomes the page's `noAccess` state. */
async function canAccessExam(examId: string, action: 'read' | 'update'): Promise<boolean> {
  try {
    await loadAndAssert(Exam, examId, action, { _id: 1 });
    return true;
  } catch (err) {
    if (err instanceof AccessDeniedError) return false;
    throw err;
  }
}

export const getStudentExamQuery = registerQuery({
  operationName: 'getStudentExamQuery',
  type: () => String,
  params: [{ name: 'examId', type: () => String }],

  resolve: (examId: string) => async (): Promise<string> => {
    const session = await CurrentUserInfo.getCurrentUserInfo()({ userID: 1 }).catch(() => null);
    if (!session?.userID) {
      return JSON.stringify({ ok: false, message: 'StudentExam.errors.notSignedIn' } satisfies StudentExamPayload);
    }

    // Online submission is a student-app-only feature — a teacher's own exams stay excluded even though
    // the ownership check below would otherwise let them open one of their own (the exam-editor's own
    // "Online ausfüllen" preview relied on exactly that; it has been removed for the same reason).
    const { resolveIsCurrentSessionStudent } = await import('../utils/exam-role.js');
    if (!(await resolveIsCurrentSessionStudent(session.userID))) {
      return JSON.stringify({ ok: false, message: 'StudentExam.errors.studentsOnly' } satisfies StudentExamPayload);
    }

    if (!(await canAccessExam(examId, 'read'))) {
      return JSON.stringify({ ok: false, message: 'StudentExam.errors.noAccess' } satisfies StudentExamPayload);
    }
    const examCollection = await getCollection(Exam);
    const exam = await examCollection.findOne({ _id: examId });
    if (!exam) {
      return JSON.stringify({
        ok: false,
        message: 'StudentExam.errors.noAccess',
      } satisfies StudentExamPayload);
    }

    const tasks = Array.isArray((exam as any).tasks) ? (exam as any).tasks : [];
    if (tasks.length === 0) {
      return JSON.stringify({
        ok: false,
        message: 'StudentExam.errors.noTasks',
      } satisfies StudentExamPayload);
    }

    const source = String((exam as any).examSource ?? '');
    if (!source) {
      // An uploaded (scanned) exam has a task tree but no typst source, so there is no page to render and
      // nothing to position against. Answering one online would need a different renderer entirely.
      return JSON.stringify({
        ok: false,
        message: 'StudentExam.errors.uploadedExam',
      } satisfies StudentExamPayload);
    }

    // The creator has not given the exam its final confirm yet, so its structure may still change under the
    // student — see `confirmExamCreationMutation`.
    if (!isExamCreationConfirmed(exam)) {
      return JSON.stringify({
        ok: false,
        message: 'StudentExam.errors.notConfirmed',
      } satisfies StudentExamPayload);
    }

    let svg: string;
    let layout: ExamLayout | undefined;
    try {
      const { compileExamSource } = await import('./exam-creation/compile-exam.js');
      const { loadPersistedAssets } = await import('./exam-creation/exam-editor-api.js');
      const { assets } = await loadPersistedAssets(examId);
      const compiled = await compileExamSource(source, assets);
      svg = compiled.svg;
      layout = compiled.layout;
    } catch (err) {
      logger.error({ err, examId }, 'Student exam page could not render the exam');
      return JSON.stringify({
        ok: false,
        message: 'StudentExam.errors.renderFailed',
      } satisfies StudentExamPayload);
    }

    const pageImageUrls = Array.isArray((exam as any).blankExamIds)
      ? ((exam as any).blankExamIds as string[]).map(id => ImageFile.getServeLink(id))
      : [];

    return JSON.stringify({
      svg,
      layout,
      pageImageUrls,
      ok: true,
      title: (exam as any).title ?? '',
      subject: (exam as any).subject ?? '',
      class: (exam as any).class ?? '',
      duration: (exam as any).duration ?? '',
      date: (exam as any).date ?? '',
      tasks,
      additionalInformation: Array.isArray((exam as any).additionalInformation)
        ? (exam as any).additionalInformation
        : [],
    } satisfies StudentExamPayload);
  },
});

/**
 * One filled-in answer, addressed by the task's FULL dotted path ("1.a.i").
 *
 * The path is computed identically on both sides by joining a task's own `taskNumber` onto its ancestors'
 * — it exists because `ExamTask.taskNumber` is relative per level, so "a" alone does not identify a task.
 * It is only ever a lookup key: what gets WRITTEN onto each `SubmissionTask` is the relative number again,
 * because that is what grading matches against (`subTasksSubmission[].taskNumber` ↔ `subTasks[].taskNumber`).
 */
export interface StudentAnswerInput {
  path: string;
  /** Typed text, or a client-rendered rendering of a structured widget ("1 - C", "[x] A) ..."). */
  text?: string;
  /** A drawing, as a `data:image/png;base64,...` URL straight off the canvas. */
  imageBase64?: string;
  /**
   * The same answer in the shape the TEMPLATE prints it back with — see `StudentAnswerPayload` in
   * answer-widgets.tsx, which is what builds these. `text` above stays the answer grading reads; these
   * exist because a tick has to go in its own checkbox and a label in its own callout box, and neither
   * position survives being joined into a string.
   */
  choice?: number[];
  /** Left row index (as a string) to every zero-based right row it was joined to — matching allows joining
   * a row to more than one on the other side, so this is a list, not a single index. */
  match?: Record<string, number[]>;
  labels?: Record<string, string>;
  /** `gapFill`/`promptList` — the blank's own 1-based printed number to what was written in it. */
  blanks?: Record<string, string>;
}

/**
 * A drawing made directly on a MATERIAL (a figure/table the exam shows, not a task's own answer region) —
 * see `layout.boxes`, keyed by material id the same way a task's own boxes are (`computeBoundingBoxes`).
 * Kept separate from `StudentAnswerInput` rather than given a fake `path`: a material can belong to several
 * tasks at once (`tasks:` on `#material(...)`), so ONE drawing here becomes one extra bounding box on EVERY
 * task that references it (see `submitStudentExamMutation`), not an answer owned by any single path.
 */
export interface StudentMaterialAnswerInput {
  materialId: string;
  imageBase64: string;
}

// ─── where on the printed page an answer was written ────────────────────────────────────────────────

/**
 * The scale a stored `BoundingBox` is expressed in: 0–1000 across the page image, on each axis
 * INDEPENDENTLY — not a preserved aspect ratio. Every reader divides by exactly this again (crop-image.ts
 * multiplies back up by the image's own pixel width and height), so it is the one number that has to agree.
 */
const BOX_SCALE = 1000;

/**
 * The rectangle a task's answers were written into, in the page's own points — or nothing, for a task with
 * no answer region at all.
 *
 * `box` for every region that has one — a real, separate answer widget with an extent of its own. That now
 * covers a labelling figure (the figure-block's own picture-plus-callouts box) and a current-source GAP_TEXT
 * passage (the `#gap-text(...)` wrapper's own box) alike — both set directly in the nesting pass in
 * exam-structure.ts, which see.
 *
 * The one remaining fallback is an OLDER GAP_TEXT source with no `#gap-text` wrapper at all — bare
 * `#gap(...)` calls loose in the task body have no widget box between them and the task's own, so "the
 * answer" there was never a region apart from the prompt to begin with: cropping just the scattered gaps (a
 * handful of tiny rects) would hand a reviewer the fill-ins with no sentence to read them in. The task's OWN
 * box — typst's, not rebuilt here — is the least-wrong thing available for that case: see `boxes`'s own doc
 * comment in exam-structure.ts ("a task's own box spans its prompt AND its answer together").
 */
function answerRect(
  region: AnswerRegion | undefined,
  taskNumber: string,
  layout: ExamLayout | undefined,
): BlockBoundingBox | undefined {
  if (!region) return undefined;
  if (region.box) return region.box;
  if (region.gapBoxes?.length) return layout?.boxes[taskNumber];
  return undefined;
}

/**
 * Typst points → what a `BoundingBox` stores: a page number and two normalized corners.
 *
 * NOTHING IS MEASURED HERE. The compile the caller just ran already recorded every answer region's exact
 * extent, in the same points the page images it hands over are rendered from, so this is arithmetic only.
 *
 * The arithmetic is the student page's own (`boxStyle` in page.tsx) against a different denominator: 1000
 * instead of 100, and a page number instead of an offset down a stacked column of pages. Both page
 * conventions are 1-based and mean the same thing, which is why `page` copies straight across — typst's
 * `here().position().page`, and the index-from-one into `Submission.imageIds` that every reader of
 * `pageNumber` assumes (update-submission-task.ts crops `imageIds[pageNumber - 1]`).
 */
/**
 * The actual arithmetic behind `answerGeometry` below, pulled out so a MATERIAL's own box (never wrapped in
 * an `AnswerRegion` — it isn't one) can be converted the same way. Same rules apply: nothing is measured
 * here, `rect` is already exact, in the same points the page images are rendered from.
 */
function rectGeometry(
  rect: BlockBoundingBox | undefined,
  layout: ExamLayout | undefined,
): { pageNumber: number; topLeft: Point; bottomRight: Point } | undefined {
  if (!layout || !rect) return undefined;

  const { pageWidthPt, pageHeightPt } = layout;
  if (!(pageWidthPt > 0) || !(pageHeightPt > 0)) return undefined;
  if (![rect.x, rect.y, rect.width, rect.height].every(value => Number.isFinite(value))) return undefined;

  const scale = (value: number, extentPt: number) =>
    Math.round(Math.min(BOX_SCALE, Math.max(0, (value / extentPt) * BOX_SCALE)));

  return {
    pageNumber: Math.max(1, Math.round(rect.page || 1)),
    topLeft: new Point({ x: scale(rect.x, pageWidthPt), y: scale(rect.y, pageHeightPt) }),
    bottomRight: new Point({
      x: scale(rect.x + rect.width, pageWidthPt),
      y: scale(rect.y + rect.height, pageHeightPt),
    }),
  };
}

function answerGeometry(
  region: AnswerRegion | undefined,
  taskNumber: string,
  layout: ExamLayout | undefined,
): { pageNumber: number; topLeft: Point; bottomRight: Point } | undefined {
  return rectGeometry(answerRect(region, taskNumber, layout), layout);
}

/**
 * The id THIS student's online submission for this exam always has — deterministic, not looked up, so
 * every resubmission recomputes the exact same value rather than having to find it.
 *
 * DELIBERATELY NOT KEYED ON `studentName`. An earlier version matched on `{examId, userID, studentName}` to
 * find a prior row, and that broke resubmission detection in practice: `studentName` is plain React state
 * on the client (student-exam/page.tsx), never persisted, so a reload before resubmitting resets it to '',
 * and retyping it a second time rarely comes out byte-identical (a trimmed space, a nickname, a fixed typo,
 * a different capitalisation) — the lookup then found nothing, treated an actual resubmission as a fresh
 * one, created a SECOND submission row instead of replacing the first, and announced "submitted" a second
 * time instead of "resubmitted". `examId` + `userID` (the signed-in account, not the typed name) is the
 * identity that is actually stable across two submits of the same exam, and it is exactly what "ONE ONLINE
 * SUBMISSION PER STUDENT PER EXAM" (see `submitStudentExamMutation`) means in the first place.
 */
function onlineSubmissionId(examId: string, userID: string): string {
  return `online-${examId}-${userID}`;
}

@Entity()
export class SubmitStudentExamResult extends AdornisEntity {
  static override _class = 'SubmitStudentExamResult';

  @Field(() => Boolean)
  success!: boolean;

  @Field(() => String)
  message!: string;

  @Field(() => String)
  submissionId!: string;
}

export const submitStudentExamMutation = registerMutation({
  operationName: 'submitStudentExamMutation',
  type: () => SubmitStudentExamResult,
  params: [
    { name: 'examId', type: () => String },
    /** `StudentAnswerInput[]`, JSON-encoded — a list of open-shaped objects has no BaseQL input type. */
    { name: 'answersJson', type: () => String },
    { name: 'studentName', type: () => String },
  ],

  resolve:
    (examId: string, answersJson: string, studentName?: string) =>
    async (_gqlFields: BaseQLSelectionSet<SubmitStudentExamResult>) => {
      const session = await CurrentUserInfo.getCurrentUserInfo()({ userID: 1 }).catch(() => null);
      if (!session?.userID) {
        return new SubmitStudentExamResult({
          success: false,
          message: 'StudentExam.errors.notSignedIn',
          submissionId: '',
        });
      }

      // Same student-only gate as getStudentExamQuery — a teacher must not be able to hand in an "answer"
      // for their own exam through this mutation, which is what a stray call from a leftover preview tab
      // would otherwise do.
      const { resolveIsCurrentSessionStudent } = await import('../utils/exam-role.js');
      if (!(await resolveIsCurrentSessionStudent(session.userID))) {
        return new SubmitStudentExamResult({
          success: false,
          message: 'StudentExam.errors.studentsOnly',
          submissionId: '',
        });
      }

      // Writes into the exam (submission + `examState`), hence 'update'.
      if (!(await canAccessExam(examId, 'update'))) {
        return new SubmitStudentExamResult({
          success: false,
          message: 'StudentExam.errors.noAccess',
          submissionId: '',
        });
      }
      const examCollection = await getCollection(Exam);
      const exam = await examCollection.findOne({ _id: examId });
      if (!exam) {
        return new SubmitStudentExamResult({
          success: false,
          message: 'StudentExam.errors.noAccess',
          submissionId: '',
        });
      }

      let answers: StudentAnswerInput[];
      let materialAnswers: StudentMaterialAnswerInput[];
      try {
        const parsed = JSON.parse(answersJson);
        // The bare-array shape is the OLD wire format (before material annotations existed) — accepted
        // alongside the current `{ answers, materials }` object so nothing breaks if a stale client tab is
        // still open when this ships.
        if (Array.isArray(parsed)) {
          answers = parsed;
          materialAnswers = [];
        } else {
          answers = Array.isArray(parsed?.answers) ? parsed.answers : [];
          materialAnswers = Array.isArray(parsed?.materials) ? parsed.materials : [];
        }
      } catch {
        return new SubmitStudentExamResult({
          success: false,
          message: 'StudentExam.errors.unreadableAnswers',
          submissionId: '',
        });
      }

      const byPath = new Map(answers.map(answer => [String(answer.path ?? ''), answer]));
      const examTasks = Array.isArray((exam as any).tasks) ? (exam as any).tasks : [];
      const resolvedStudentName = (studentName ?? '').trim() || 'Online-Abgabe';

      // EVERY DRAWING DECODED ONCE, for two consumers: the bytes go into the compile below, so the sketch
      // is printed into the answer space it was drawn in, and the same bytes become a stored ImageFile so
      // grading can read the picture directly (`contentType: 'image'` boxes contribute their `imageId`).
      const drawingBytesByPath = new Map<string, Buffer>();
      for (const answer of answers) {
        const dataUrl = typeof answer.imageBase64 === 'string' ? answer.imageBase64 : '';
        const base64 = dataUrl.includes(',') ? dataUrl.slice(dataUrl.indexOf(',') + 1) : '';
        if (base64) drawingBytesByPath.set(String(answer.path), Buffer.from(base64, 'base64'));
      }

      // Same decode, for annotations drawn on a MATERIAL rather than in a task's own answer region — see
      // `StudentMaterialAnswerInput`. These never go into `renderAnswers` below: a material sits wherever it
      // was printed, not in any one task's answer space, so there is nothing sane to stamp it back onto in
      // the reprinted submission document. They become extra grading `boundingBoxes` instead, one on every
      // task that references the material — see `materialLinksByPath` further down.
      const materialBytesById = new Map<string, Buffer>();
      for (const material of materialAnswers) {
        const materialId = String(material?.materialId ?? '');
        const dataUrl = typeof material?.imageBase64 === 'string' ? material.imageBase64 : '';
        const base64 = dataUrl.includes(',') ? dataUrl.slice(dataUrl.indexOf(',') + 1) : '';
        if (materialId && base64) materialBytesById.set(materialId, Buffer.from(base64, 'base64'));
      }

      // WHAT THE TEMPLATE PRINTS, keyed by task number — which is exactly what `path` already is (both
      // sides build it by joining a task's relative number onto its ancestors'). `text` carries the same
      // rendered string grading reads; the structured fields beside it are what put a tick in its own
      // checkbox and a label in its own box, neither of which survives being joined into a string.
      const renderAnswers: ExamAnswers = {};
      for (const answer of answers) {
        const path = String(answer.path ?? '');
        if (!path) continue;
        const entry: ExamAnswers[string] = {};
        // A structured answer's `text` is the RENDERED form ("[x] A) ...", "1 - B") — right for grading,
        // wrong to print on the sheet, where the tick and the pairing are drawn where they belong. Only a
        // genuinely typed answer goes through as text.
        const isStructured = Boolean(answer.choice?.length || answer.match || answer.labels || answer.blanks);
        if (!isStructured && typeof answer.text === 'string' && answer.text.trim()) entry.text = answer.text.trim();
        if (answer.choice?.length) entry.choice = answer.choice;
        if (answer.match && Object.keys(answer.match).length > 0) entry.match = answer.match;
        if (answer.labels && Object.keys(answer.labels).length > 0) entry.labels = answer.labels;
        if (answer.blanks && Object.keys(answer.blanks).length > 0) entry.blanks = answer.blanks;
        const drawing = drawingBytesByPath.get(path);
        if (drawing) entry.drawing = drawing;
        if (Object.keys(entry).length > 0) renderAnswers[path] = entry;
      }

      /** The submission document itself, kept on the submission so a teacher can download what was handed in. */
      let submissionPdfId = '';

      // THE SUBMISSION DOCUMENT — the exam's own source compiled a second time with the answers printed
      // into it, and the pages, the geometry and the answers therefore all come out of one render.
      //
      // WHY COMPILE AT ALL RATHER THAN READ `examLayoutJson`: that is a snapshot of the exam as last
      // SAVED, and it goes stale in two independent ways. When the layout code changes, an exam saved
      // before `inline` answers resolved their widget by nesting has no `box` for its #mc or #grid-paper
      // at all, so every choice and every sketch lands at the (-1, -1) a box gets when nobody sets one
      // (that was a real bug, not a hypothetical). When the TEMPLATE changes, the same source paginates
      // differently, so the stored page images are pictures of a sheet the student was never shown.
      //
      // A LONGER ANSWER GROWS ITS REGION (see `answer-lines`), so this render can be a page longer than
      // the blank exam — which is exactly why the geometry is read back from THIS compile and the pages
      // are rasterised from THIS pdf. Nothing is compared against the blank sheet, so nothing has to
      // match it.
      const source = String((exam as any).examSource ?? '');
      let layout: ExamLayout | undefined;
      let pageImageIds: string[] = [];
      try {
        const { compileExamSource } = await import('./exam-creation/compile-exam.js');
        const { loadPersistedAssets } = await import('./exam-creation/exam-editor-api.js');
        const { PDFFile } = await import('@adornis/pdf/db/pdf-file.js');
        const { extractImagesFromPDFMutation } = await import('./file-intake.js');

        const { assets } = await loadPersistedAssets(examId);
        const compiled = await compileExamSource(source, assets, renderAnswers);
        layout = compiled.layout;

        const pdf = await PDFFile.createFromBuffer(
          compiled.pdf,
          `${(exam as any).title || 'exam'} — ${resolvedStudentName}.pdf`,
        );
        submissionPdfId = pdf._id;
        pageImageIds = await extractImagesFromPDFMutation(pdf._id)();
        logger.info({ examId, pages: pageImageIds.length }, 'Rendered the submission document');
      } catch (err) {
        // Positions and pages are a nicety, the answers are not: a submission without them still grades,
        // it just cannot be shown on the page it was written on.
        logger.error({ err, examId }, 'Could not render the submission — submitting without pages');
      }

      // Every drawing also becomes a real ImageFile: `buildSubmissionTask` below is recursive and
      // synchronous-shaped, and awaiting an upload per node inside it would serialise every branch.
      const imageIdByPath = new Map<string, string>();
      await Promise.all(
        [...drawingBytesByPath].map(async ([path, bytes]) => {
          try {
            const image = await ImageFile.createFromBuffer(
              bytes,
              `student-answer-${examId}-${path.replace(/[^\w.-]/gu, '_')}.png`,
            );
            await image.save();
            imageIdByPath.set(path, image._id);
          } catch (err) {
            // One unreadable drawing must not lose the rest of the exam — the task keeps its typed text
            // and simply arrives without the picture, which grading reports as a thin answer rather than
            // failing the submit outright.
            logger.error({ err, examId, path }, 'Could not persist a student drawing');
          }
        }),
      );

      // Same upload, for material annotations — a separate map because a material image is keyed by
      // material id, not by task path (see `StudentMaterialAnswerInput`).
      const materialImageIdById = new Map<string, string>();
      await Promise.all(
        [...materialBytesById].map(async ([materialId, bytes]) => {
          try {
            const image = await ImageFile.createFromBuffer(
              bytes,
              `student-answer-${examId}-material-${materialId.replace(/[^\w.-]/gu, '_')}.png`,
            );
            await image.save();
            materialImageIdById.set(materialId, image._id);
          } catch (err) {
            logger.error({ err, examId, materialId }, 'Could not persist a student material annotation');
          }
        }),
      );

      // Every task a material with a submitted annotation is linked to — mirrors `task.additionalInfoIds`
      // AND `task.inlineFigureIds` on the client (`page.tsx`'s `materialSlots` treats both the same way:
      // just ids into `layout.boxes`), walked here the same recursive way `buildSubmissionTask` below
      // builds paths, so the two agree on what a task's path is.
      const materialIdsByPath = new Map<string, string[]>();
      const linkMaterialsToPath = (task: any, parentPath: string): void => {
        const path = parentPath ? `${parentPath}.${task?.taskNumber ?? ''}` : String(task?.taskNumber ?? '');
        const ids = [
          ...(Array.isArray(task?.additionalInfoIds) ? task.additionalInfoIds : []),
          ...(Array.isArray(task?.inlineFigureIds) ? task.inlineFigureIds : []),
        ];
        const linked = ids.filter((id: unknown) => typeof id === 'string' && materialImageIdById.has(id));
        if (linked.length > 0) materialIdsByPath.set(path, linked);
        for (const subTask of Array.isArray(task?.subTasks) ? task.subTasks : []) linkMaterialsToPath(subTask, path);
      };
      for (const task of examTasks) linkMaterialsToPath(task, '');

      /** Answered tasks whose answer could be placed on the page — see the log line at the end. */
      let positioned = 0;

      /** Mirrors `createSubmissionTasks`: a node for EVERY exam task, answered or not, nested the same way. */
      const buildSubmissionTask = (task: any, parentPath: string): SubmissionTask => {
        const relativeNumber = String(task?.taskNumber ?? '');
        const path = parentPath ? `${parentPath}.${relativeNumber}` : relativeNumber;
        const answer = byPath.get(path);

        // Where this task's answer space is printed — shared by both boxes below, because a task that got
        // typed text AND a drawing got them in the one place the sheet left for it.
        const geometry = answerGeometry(layout?.answers?.[path], path, layout);

        const boxes: BoundingBox[] = [];
        const text = typeof answer?.text === 'string' ? answer.text.trim() : '';
        if (text) {
          boxes.push(
            new BoundingBox({
              ...geometry,
              contentType: 'text',
              ocrExtractedText: text,
              // Typed by the student, so there is nothing uncertain to review: not handwriting, not an
              // OCR guess. `approved` keeps it out of the extraction-review queue in the canvas.
              isHandwritten: false,
              confidence: 1,
              approved: true,
            }),
          );
        }

        const imageId = imageIdByPath.get(path);
        if (imageId) {
          boxes.push(
            new BoundingBox({
              ...geometry,
              contentType: 'image',
              // The drawing itself, NOT a crop of the page: on the scan path `imageId` points at the
              // region cut out of the page image, and a pointer-drawn answer is already exactly that.
              imageId,
              ocrExtractedText: '',
              // Drawn by hand, just with a pointer instead of a pen — the grading prompt treats a
              // handwritten region differently, and a sketch is exactly that case.
              isHandwritten: true,
              confidence: 1,
              approved: true,
            }),
          );
        }

        // A material this task references, annotated by the student — its OWN box (wherever it was
        // printed, not this task's answer space), one extra `image` box per linked material. See
        // `materialIdsByPath`/`StudentMaterialAnswerInput`.
        for (const materialId of materialIdsByPath.get(path) ?? []) {
          const materialGeometry = rectGeometry(layout?.boxes?.[materialId], layout);
          const materialImageId = materialImageIdById.get(materialId);
          if (!materialGeometry || !materialImageId) continue;
          boxes.push(
            new BoundingBox({
              ...materialGeometry,
              contentType: 'image',
              imageId: materialImageId,
              ocrExtractedText: '',
              isHandwritten: true,
              confidence: 1,
              approved: true,
            }),
          );
        }

        if (geometry && boxes.length > 0) positioned += 1;

        const subTasks = Array.isArray(task?.subTasks) ? task.subTasks : [];

        // Same list a grading REPORT reads pictures from (`generate-grading-reports.ts`), not just the
        // grading prompt itself (which reads `boundingBoxes` — see this file's own header) — a material
        // annotation belongs in both, the same way the task's own drawing already does.
        const materialImageIds = (materialIdsByPath.get(path) ?? [])
          .map(materialId => materialImageIdById.get(materialId))
          .filter((id): id is string => Boolean(id));

        return new SubmissionTask({
          _id: crypto.randomUUID(),
          taskNumber: relativeNumber,
          imageIds: imageId ? [imageId, ...materialImageIds] : materialImageIds,
          boundingBoxes: boxes,
          studentAnswer: aggregateOCRFromBoundingBoxes(boxes),
          subTasksSubmission: subTasks.map((subTask: any) => buildSubmissionTask(subTask, path)),
          gradingState: TaskGradingState.PENDING,
        });
      };

      const taskSubmissions = examTasks.map((task: any) => buildSubmissionTask(task, ''));

      // ONE ONLINE SUBMISSION PER STUDENT PER EXAM — handing the same form in again REPLACES what was
      // there rather than adding a second entry beside it, because that is what "abgeben" means to the
      // person doing it: the sheet they just filled in is their answer, not their latest answer.
      //
      // BY REUSING THE SAME DETERMINISTIC `_id` (see `onlineSubmissionId`), not by looking up a previous
      // row and deleting it. A find-then-write can only ever REACT to a prior row it happened to match;
      // recomputing the same id needs no match at all, which is what makes resubmission detection (below)
      // correct even when nothing about a prior row was findable.
      const submissionId = onlineSubmissionId(examId, session.userID);
      const { getRawCollection } = await import('@adornis/baseql/server/collections.js');
      const rawSubmissions = await getRawCollection<any>(Submission._collectionName);
      const isResubmission = await rawSubmissions
        .findOne({ _id: submissionId } as any, { projection: { _id: 1 } })
        .then(Boolean)
        .catch(err => {
          // Not knowing must not fail the hand-in, and reads as "first submission" rather than
          // "resubmission" — the safer of the two wrong guesses, since it never claims a replace happened
          // when it didn't.
          logger.warn({ err, examId }, 'Could not check for a previous online submission');
          return false;
        });
      const submission = new Submission({
        _id: submissionId,
        examId,
        submittedAt: new Date().toISOString(),
        // THE PAGES OF THE SUBMISSION DOCUMENT — the exam with this student's answers printed into it, not
        // the blank sheet. A scanned submission's pages are the paper the student wrote on; these are the
        // same thing, typeset instead of photographed, and every box above is positioned against exactly
        // these out of the one compile that produced them.
        imageIds: pageImageIds,
        // Never re-extract this one — see the field's own note on Submission.
        answersSubmittedOnline: true,
        // The compiled sheet those pages came from, kept so the hand-in can be downloaded as one document.
        submissionPdfId,
        userID: session.userID,
        studentName: resolvedStudentName,
        // There was never a multi-student PDF to split, so there is nothing for the teacher to confirm —
        // leaving this false would park the submission in the split-confirmation canvas forever.
        splitConfirmed: true,
        state: 'COMPLETED' as any,
        taskSubmissions,
      } as any);

      const submissionData = JSON.parse(JSON.stringify(submission.toObject()));
      // `toObject()` walks registered @Fields only, and `_class` is a static — so a nested entity loses it
      // and has to be written back by hand. Same fixup, same reason, as in create-submission.ts. There was
      // no split to evaluate here (one student, one form), which is what a confidence of 1 with no issues
      // says; the field is non-optional, so leaving it off would hand the canvas an undefined to read.
      submissionData.splitEvaluationResult = {
        _class: splitEvaluationResult._class,
        confidence: 1,
        issues: [],
      };

      // Upsert rather than insert: `submissionId` is always this student's one deterministic id for this
      // exam (see `onlineSubmissionId`), so this single write replaces the prior attempt when there was
      // one and creates it when there wasn't — there is no second row to ever clean up. Raw, because
      // replacing a whole document is not something the entity collection exposes (same seam as
      // `clearPreviousAttempt` in submission-split.ts); the change stream the teacher's canvas watches sees
      // it either way. Same `rawSubmissions` handle the resubmission check above already opened.
      await rawSubmissions.replaceOne({ _id: submissionId } as any, submissionData, { upsert: true });

      logger.info(
        {
          submissionId,
          examId,
          taskCount: taskSubmissions.length,
          drawings: imageIdByPath.size,
          pages: pageImageIds.length,
          positioned,
          replaced: isResubmission,
        },
        'Created submission from the student exam page',
      );

      // `examState.onlineSubmissionReceived` is the cheap exam-level flag `hasReceivedAnySubmission`
      // (exam-lock.ts) and the master-solution/grading-scheme tool guards read — set unconditionally, a
      // resubmission included, since it only ever needs to go from false to true once.
      try {
        await examCollection.updateOne({ _id: examId }, { $set: { 'examState.onlineSubmissionReceived': true } });
      } catch (err) {
        logger.warn({ err, examId }, 'Could not mark the exam as having received an online submission');
      }

      // A RE-submission that overwrites an already-graded attempt is exactly the case
      // updateSubmissionTaskMutation already handles for a teacher's manual edit — see its own
      // `shouldTriggerRegrading`/`hasGradingStarted` gate in update-submission-task.ts, reused here so
      // re-drawing an answer after grading started behaves the same way whichever side changed it.
      if (isResubmission && (exam as any)?.examState?.hasGradingStarted) {
        try {
          const { dispatchGradingWave } = await import('./jobs/dispatch-grading.js');
          void dispatchGradingWave(examId, { submissionIds: [submissionId] }).catch(err =>
            logger.error({ err, examId, submissionId }, 'Regrading the resubmitted online exam failed'),
          );
        } catch (err) {
          logger.error({ err, examId, submissionId }, 'Could not dispatch a regrade for the resubmitted online exam');
        }
      }

      // ANNOUNCES INTO THE CHAT, same `pushWorkflowTask` mechanism exam-creation.job.ts uses for its own
      // finished announcement — the agent confirms receipt and offers what to do next (generate a master
      // solution/grading scheme, or start grading) rather than the student having to ask.
      try {
        // TASK_FRAME/purely-structural nodes carry no answer of their own (`needsAnswer === false`, the same
        // flag every other reader of the exam tree gates on — see `solution-completeness.ts`) — counting them
        // here would inflate the denominator with tasks nobody was ever going to answer.
        const countTasks = (nodes: any[]): number =>
          nodes.reduce(
            (sum, node) =>
              sum +
              (node?.needsAnswer !== false ? 1 : 0) +
              countTasks(Array.isArray(node?.subTasks) ? node.subTasks : []),
            0,
          );
        const { pushWorkflowTask } = await import('./pending-tasks/workflow-agent.js');
        const { createOnlineSubmissionReceivedPendingTask } =
          await import('./pending-tasks/online-submission-received.task.js');
        await pushWorkflowTask(
          examId,
          (exam as any)?.conversationId,
          createOnlineSubmissionReceivedPendingTask({
            studentName: resolvedStudentName,
            isResubmission,
            answeredCount: positioned,
            totalCount: countTasks(examTasks),
          }),
        );
      } catch (err) {
        logger.warn({ err, examId }, 'Failed to announce the online submission');
      }

      // Nothing renders this one — the page swaps to its own "submitted" state on success — but the field
      // is non-optional, so it carries the same kind of key every other message here does.
      return new SubmitStudentExamResult({
        success: true,
        message: 'StudentExam.submittedTitle',
        submissionId,
      });
    },
});
